<template>
  <div />
</template>
<script>
export default {
  props: {
    urlBase: {
      type: String,
      required: false, // only needed for REST
      default: ""
    },
    type: {
      type: String,
      required: false,
      default: "REST"
    },
    fields: {
      type: Array, // [{name:'', alias:''}.{...}]
      required: true
    },
    active: {
      type: Boolean,
      required: true
    },
    projection: {
      type: Object,
      required: false,
      default: () => {
        return {
          code: "EPSG:3857",
          proj4def:
            "+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +a=6378137 +b=6378137 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
        };
      }
    }
  },
  data() {
    return {
      protocols: { REST: "REST", WMS: "WMS" },
      map: this.$parent.map
    };
  },
  watch: {
    active() {
      if (this.active) {
        this.init();
      } else {
        this.removeControl();
      }
    }
  },
  destroyed() {
    this.removeControl();
  },
  mounted() {
    if (this.active) {
      this.init();
    }
  },
  methods: {
    init() {
      switch (this.type) {
        case this.protocols.REST: {
          this.map.on("click", this.setupRESTService());
          break;
        }
        case this.protocols.WMS: {
          this.map.on("click", this.setupWMSService());
          break;
        }
      }
    },
    setupRESTService() {
      const self = this;
      return e => {
        if (self.active) {
          var url = `${this.urlBase}?geom=POINT(${e.latlng.lng} ${
            e.latlng.lat
          })`;
          fetch(url)
            .then(response => {
              if (!response.ok) {
                throw Error(response.statusText);
              }
              return response.text();
            })
            .then(jsonData => {
              const data = JSON.parse(jsonData);
              const content = self.createContentFromJson(data);
              self.openPopup(e.latlng, content);
            })
            .catch(() => {
              console.log("No data in this point");
            });
        }
      };
    },
    setupWMSService() {
      const self = this;
      return function(e) {
        if (
          self.active &&
          e.originalEvent.target.className !== "leaflet-sbs-range" //Workaround to fix sidebyside control propragating click event
        ) {
          var url = self.getFeatureInfoUrl(
            self.map,
            e.latlng,
            {},
            self.$parent.wmsLayer
          );
          // popup with two layers values
          if (Object.keys(self.$parent.wmsSecondaryLayer).length === 0) {
            self.singleLayerPopup(e, url);
          } else {
            var urlSecondary = self.getFeatureInfoUrl(
              self.map,
              e.latlng,
              {},
              self.$parent.wmsSecondaryLayer
            );
            self.twoLayersPopup(e, url, urlSecondary);
          }
        }
      };
    },
    singleLayerPopup(e, url) {
      const self = this;
      fetch(url)
        .then(response => {
          return response.text();
        })
        .then(function(xml) {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xml, "text/xml");
          var content = self.createContentFromXml(xmlDoc);
          self.openPopup(e.latlng, content);
        });
    },
    twoLayersPopup(e, url, urlSecondary) {
      ///TODO: cambiar la concatenación de peticiones asíncronas
      const self = this;
      fetch(url)
        .then(response => {
          return response.text();
        })
        .then(function(xml) {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xml, "text/xml");
          fetch(urlSecondary)
            .then(response => {
              return response.text();
            })
            .then(function(xmlSecondary) {
              const parser = new DOMParser();
              const xmlDocSecondary = parser.parseFromString(
                xmlSecondary,
                "text/xml"
              );
              var content = self.createTwoLayersContentFromXml(
                xmlDoc,
                xmlDocSecondary
              );
              self.openPopup(e.latlng, content);
            });
        });
    },
    createContentFromJson(data) {
      let content = "";
      for (const field of this.fields) {
        if (data.hasOwnProperty(field.name)) {
          const value = data[field.name];
          if (!isNaN(value)) {
            const fieldHtml = `<h5>${field.alias}: ${value}${field.units}</h5>`;
            content = content + fieldHtml;
          }
        }
      }
      return content;
    },
    createContentFromXml(xmlDoc) {
      let content = "";
      for (const field of this.fields) {
        const contentValue = xmlDoc.getElementsByTagName(field.name)[0]
          .textContent;
        const value = this._getValue(field, contentValue);
        const fieldHtml = `<h5>${field.alias}: ${value}${field.units}</h5>`;
        content = content + fieldHtml;
      }
      return content;
    },
    createTwoLayersContentFromXml(xmlDoc, xmlSecondaryDoc) {
      let content = "";
      for (const field of this.fields) {
        const contentValue = xmlDoc.getElementsByTagName(field.name)[0]
          .textContent;
        const contentSecundaryValue = xmlSecondaryDoc.getElementsByTagName(
          field.name
        )[0].textContent;

        content =
          content +
          this._createTwoLayersFieldHtml(
            field,
            contentValue,
            contentSecundaryValue
          );
      }
      return content;
    },
    _createTwoLayersFieldHtml(field, contentValue, contentSecundaryValue) {
      const value = this._getValue(field, contentValue);
      const valueSec = this._getValue(field, contentSecundaryValue);

      const fieldHtml = `<h5>${field.alias}: ${value}${
        value !== "No Data" ? field.units : ""
      } | ${valueSec}${valueSec !== "No Data" ? field.units : ""}</h5>`;

      return fieldHtml;
    },
    _getValue(field, contentValue) {
      let value;
      if (Number.isNaN(parseFloat(contentValue))) {
        value = "No Data";
      } else {
        value = parseFloat(contentValue).toFixed(2);
        if (field.toUnits && field.units === "%") {
          value = value * 100;
        }
      }
      return value;
    },
    openPopup(latlng, content) {
      L.popup()
        .setLatLng(latlng)
        .setContent(content)
        .openOn(this.map);
    },
    getFeatureInfoUrl(map, latlng, params, layer) {
      // Build the URL for a GetFeatureInfo
      var crs = new L.Proj.CRS(this.projection.code, this.projection.proj4def);
      var point = map.latLngToContainerPoint(latlng, map.getZoom());
      var size = map.getSize();
      var bounds = map.getBounds();
      var sw = bounds.getSouthWest();
      var ne = bounds.getNorthEast();
      sw = crs.projection._proj.forward([sw.lng, sw.lat]);
      ne = crs.projection._proj.forward([ne.lng, ne.lat]);

      const defaultParams = {
        request: "GetFeatureInfo",
        service: "WMS",
        srs: layer._crs.code,
        styles: "",
        version: "1.1.1",
        format: layer.options.format,
        bbox: [sw.join(","), ne.join(",")].join(","),
        height: size.y,
        width: size.x,
        layers: layer.options.layers,
        query_layers: layer.options.layers,
        info_format: "text/xml"
      };

      params = L.Util.extend(defaultParams, params || {});
      params[params.version === "1.3.0" ? "i" : "x"] = parseInt(point.x);
      params[params.version === "1.3.0" ? "j" : "y"] = parseInt(point.y);

      return layer._url + L.Util.getParamString(params, layer._url, true);
    },
    removeControl() {
      this.map.off("click"); // remove event listener for identify
    }
  }
};
</script>
<style scoped></style>
